import {
  LogicRule,
  LogicRuleGroup,
  LogicCondition,
  LogicAction,
  QuestionDefinition,
} from '../types/survey.types';

/**
 * Evaluates a single logic condition against a response value
 */
export const evaluateCondition = (
  condition: LogicCondition,
  responseValue: any
): boolean => {
  const { operator, value } = condition;

  // For compound types, extract the primary numeric value for comparisons
  let effectiveValue = responseValue;
  if (responseValue && typeof responseValue === 'object' && !Array.isArray(responseValue)) {
    if ('daysOn' in responseValue) effectiveValue = responseValue.daysOn;
    else if ('amount' in responseValue) effectiveValue = responseValue.amount;
  }

  // Handle empty/null responses
  if (effectiveValue === null || effectiveValue === undefined || effectiveValue === '') {
    if (operator === 'is_empty') return true;
    if (operator === 'is_not_empty') return false;
    return false; // All other operators return false for empty values
  }

  // Handle is_not_empty
  if (operator === 'is_not_empty') {
    return true;
  }

  // Convert to strings for string operations
  const strResponse = String(effectiveValue);
  const strValue = String(value);

  // Handle operators
  switch (operator) {
    case 'equals':
      // Handle array values (for checkbox questions)
      if (Array.isArray(effectiveValue)) {
        return Array.isArray(value)
          ? JSON.stringify(effectiveValue.sort()) === JSON.stringify(value.sort())
          : effectiveValue.includes(strValue);
      }
      return strResponse === strValue;

    case 'not_equals':
      if (Array.isArray(effectiveValue)) {
        return Array.isArray(value)
          ? JSON.stringify(effectiveValue.sort()) !== JSON.stringify(value.sort())
          : !effectiveValue.includes(strValue);
      }
      return strResponse !== strValue;

    case 'contains':
      if (Array.isArray(effectiveValue)) {
        return effectiveValue.some((v) => String(v).toLowerCase().includes(strValue.toLowerCase()));
      }
      return strResponse.toLowerCase().includes(strValue.toLowerCase());

    case 'not_contains':
      if (Array.isArray(effectiveValue)) {
        return !effectiveValue.some((v) => String(v).toLowerCase().includes(strValue.toLowerCase()));
      }
      return !strResponse.toLowerCase().includes(strValue.toLowerCase());

    case 'greater_than':
      const numResponse1 = parseFloat(strResponse);
      const numValue1 = parseFloat(strValue);
      if (isNaN(numResponse1) || isNaN(numValue1)) return false;
      return numResponse1 > numValue1;

    case 'less_than':
      const numResponse2 = parseFloat(strResponse);
      const numValue2 = parseFloat(strValue);
      if (isNaN(numResponse2) || isNaN(numValue2)) return false;
      return numResponse2 < numValue2;

    case 'greater_or_equal':
      const numResponse3 = parseFloat(strResponse);
      const numValue3 = parseFloat(strValue);
      if (isNaN(numResponse3) || isNaN(numValue3)) return false;
      return numResponse3 >= numValue3;

    case 'less_or_equal':
      const numResponse4 = parseFloat(strResponse);
      const numValue4 = parseFloat(strValue);
      if (isNaN(numResponse4) || isNaN(numValue4)) return false;
      return numResponse4 <= numValue4;

    case 'is_empty':
      return false; // Already handled at the top

    default:
      console.warn(`Unknown operator: ${operator}`);
      return false;
  }
};

/**
 * Evaluates a rule group (multiple conditions with AND/OR)
 */
export const evaluateRuleGroup = (
  ruleGroup: LogicRuleGroup,
  responses: Record<string, any>
): boolean => {
  if (ruleGroup.conditions.length === 0) return false;

  const results = ruleGroup.conditions.map((condition) => {
    const responseValue = responses[condition.questionId];
    return evaluateCondition(condition, responseValue);
  });

  // Apply AND/OR logic
  if (ruleGroup.operator === 'AND') {
    return results.every((result) => result === true);
  } else {
    // OR
    return results.some((result) => result === true);
  }
};

/**
 * Evaluates a complete logic rule (multiple rule groups)
 */
export const evaluateRule = (
  rule: LogicRule,
  responses: Record<string, any>
): boolean => {
  if (!rule.enabled) return false;
  if (rule.ruleGroups.length === 0) return false;

  const groupResults = rule.ruleGroups.map((group) =>
    evaluateRuleGroup(group, responses)
  );

  // Apply group operator (AND/OR between groups)
  if (rule.groupOperator === 'AND') {
    return groupResults.every((result) => result === true);
  } else {
    // OR
    return groupResults.some((result) => result === true);
  }
};

/**
 * Applies logic actions to modify question visibility and requirements
 */
export interface QuestionState {
  visible: boolean;
  required: boolean;
}

export const applyLogicActions = (
  actions: LogicAction[],
  questionStates: Map<string, QuestionState>
): Map<string, QuestionState> => {
  const newStates = new Map(questionStates);

  actions.forEach((action) => {
    const targetId = action.targetQuestionId;
    if (!targetId) return;

    const currentState = newStates.get(targetId) || { visible: true, required: false };

    switch (action.type) {
      case 'show':
        newStates.set(targetId, { ...currentState, visible: true });
        break;
      case 'hide':
        newStates.set(targetId, { ...currentState, visible: false });
        break;
      case 'require':
        newStates.set(targetId, { ...currentState, required: true });
        break;
      case 'optional':
        newStates.set(targetId, { ...currentState, required: false });
        break;
      case 'jump_to':
        // Jump logic handled separately in the survey flow
        break;
    }
  });

  return newStates;
};

/**
 * Evaluates all logic rules for all questions in a survey
 * Returns the current state of all questions based on responses
 */
export const evaluateAllLogic = (
  questions: QuestionDefinition[],
  responses: Record<string, any>
): Map<string, QuestionState> => {
  // First pass: find all questions that are targets of "show" actions
  // These should start as hidden and only become visible when their rule triggers
  const questionsWithShowActions = new Set<string>();

  questions.forEach((question) => {
    if (!question.logicRules || question.logicRules.length === 0) return;

    question.logicRules.forEach((rule) => {
      if (!rule.enabled) return;

      rule.actions.forEach((action) => {
        if (action.type === 'show' && action.targetQuestionId) {
          questionsWithShowActions.add(action.targetQuestionId);
        }
      });
    });
  });

  // Initialize question states
  const questionStates = new Map<string, QuestionState>();
  questions.forEach((q) => {
    // Questions with "show" actions start hidden, all others start visible
    const initiallyVisible = !questionsWithShowActions.has(q.id);

    questionStates.set(q.id, {
      visible: initiallyVisible,
      required: q.required,
    });
  });

  // Process each question's logic rules
  questions.forEach((question) => {
    if (!question.logicRules || question.logicRules.length === 0) return;

    // Evaluate each rule
    question.logicRules.forEach((rule) => {
      const ruleMatches = evaluateRule(rule, responses);

      if (ruleMatches) {
        // Apply the rule's actions
        const updatedStates = applyLogicActions(rule.actions, questionStates);
        updatedStates.forEach((state, questionId) => {
          questionStates.set(questionId, state);
        });
      }
    });
  });

  return questionStates;
};

/**
 * Gets the next visible question ID after the current one
 */
export const getNextVisibleQuestion = (
  questions: QuestionDefinition[],
  currentQuestionId: string,
  questionStates: Map<string, QuestionState>
): string | null => {
  const currentIndex = questions.findIndex((q) => q.id === currentQuestionId);
  if (currentIndex === -1) return null;

  // Find next visible question
  for (let i = currentIndex + 1; i < questions.length; i++) {
    const question = questions[i];
    const state = questionStates.get(question.id);
    if (state?.visible !== false) {
      return question.id;
    }
  }

  return null; // No more visible questions
};

/**
 * Gets all visible questions in order
 */
export const getVisibleQuestions = (
  questions: QuestionDefinition[],
  questionStates: Map<string, QuestionState>
): QuestionDefinition[] => {
  return questions.filter((q) => {
    const state = questionStates.get(q.id);
    return state?.visible !== false;
  });
};

/**
 * Validates that all required visible questions have been answered
 */
export const validateResponses = (
  questions: QuestionDefinition[],
  responses: Record<string, any>,
  questionStates: Map<string, QuestionState>
): { isValid: boolean; missingQuestions: string[] } => {
  const missingQuestions: string[] = [];

  questions.forEach((question) => {
    const state = questionStates.get(question.id);
    const isVisible = state?.visible !== false;
    const isRequired = state?.required === true;

    if (isVisible && isRequired) {
      const response = responses[question.id];
      const isEmpty = response === null || response === undefined || response === '';

      if (isEmpty) {
        missingQuestions.push(question.id);
      }
    }
  });

  return {
    isValid: missingQuestions.length === 0,
    missingQuestions,
  };
};

/**
 * Helper to get human-readable rule description
 */
export const getRuleDescription = (
  rule: LogicRule,
  questions: QuestionDefinition[]
): string => {
  const getQuestionTitle = (questionId: string) => {
    const q = questions.find((question) => question.id === questionId);
    return q ? q.title : `Question ${questionId}`;
  };

  const describeCondition = (condition: LogicCondition): string => {
    const questionTitle = getQuestionTitle(condition.questionId);
    const operatorText = {
      equals: 'equals',
      not_equals: 'does not equal',
      contains: 'contains',
      not_contains: 'does not contain',
      greater_than: 'is greater than',
      less_than: 'is less than',
      greater_or_equal: 'is greater than or equal to',
      less_or_equal: 'is less than or equal to',
      is_empty: 'is empty',
      is_not_empty: 'is not empty',
    }[condition.operator] || condition.operator;

    if (condition.operator === 'is_empty' || condition.operator === 'is_not_empty') {
      return `"${questionTitle}" ${operatorText}`;
    }

    return `"${questionTitle}" ${operatorText} "${condition.value}"`;
  };

  const describeGroup = (group: LogicRuleGroup): string => {
    const conditionDescriptions = group.conditions.map(describeCondition);
    return conditionDescriptions.join(` ${group.operator} `);
  };

  const groupDescriptions = rule.ruleGroups.map((group) => `(${describeGroup(group)})`);
  const conditionsText = groupDescriptions.join(` ${rule.groupOperator} `);

  const actionDescriptions = rule.actions.map((action) => {
    const targetTitle = action.targetQuestionId
      ? getQuestionTitle(action.targetQuestionId)
      : '';

    switch (action.type) {
      case 'show':
        return `show "${targetTitle}"`;
      case 'hide':
        return `hide "${targetTitle}"`;
      case 'require':
        return `require "${targetTitle}"`;
      case 'optional':
        return `make "${targetTitle}" optional`;
      case 'jump_to':
        return `jump to "${targetTitle}"`;
      default:
        return action.type;
    }
  });

  const actionsText = actionDescriptions.join(', ');

  return `IF ${conditionsText} THEN ${actionsText}`;
};

/**
 * Detects circular dependencies in logic rules
 * Returns an array of circular dependency chains
 */
export const detectCircularDependencies = (
  questions: QuestionDefinition[]
): string[][] => {
  const circularChains: string[][] = [];
  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  // Build a dependency graph: questionId -> [questionIds it affects]
  const dependencyGraph = new Map<string, Set<string>>();

  questions.forEach((question) => {
    if (!question.logicRules || question.logicRules.length === 0) return;

    question.logicRules.forEach((rule) => {
      if (!rule.enabled) return;

      // Get all questions referenced in conditions
      const conditionQuestions = new Set<string>();
      rule.ruleGroups.forEach((group) => {
        group.conditions.forEach((condition) => {
          conditionQuestions.add(condition.questionId);
        });
      });

      // Get all questions affected by actions
      const targetQuestions = new Set<string>();
      rule.actions.forEach((action) => {
        if (action.targetQuestionId) {
          targetQuestions.add(action.targetQuestionId);
        }
      });

      // For each question in conditions, add edges to target questions
      conditionQuestions.forEach((conditionQuestionId) => {
        if (!dependencyGraph.has(conditionQuestionId)) {
          dependencyGraph.set(conditionQuestionId, new Set());
        }
        targetQuestions.forEach((targetId) => {
          dependencyGraph.get(conditionQuestionId)!.add(targetId);
        });
      });
    });
  });

  // DFS to detect cycles
  const dfs = (questionId: string, path: string[]): boolean => {
    visited.add(questionId);
    recursionStack.add(questionId);
    path.push(questionId);

    const dependencies = dependencyGraph.get(questionId);
    if (dependencies) {
      const dependencyArray = Array.from(dependencies);
      for (const dependentId of dependencyArray) {
        if (!visited.has(dependentId)) {
          if (dfs(dependentId, path)) {
            return true;
          }
        } else if (recursionStack.has(dependentId)) {
          // Found a cycle
          const cycleStart = path.indexOf(dependentId);
          const cycle = path.slice(cycleStart);
          cycle.push(dependentId); // Complete the cycle
          circularChains.push(cycle);
          return true;
        }
      }
    }

    path.pop();
    recursionStack.delete(questionId);
    return false;
  };

  // Check each question for cycles
  questions.forEach((question) => {
    if (!visited.has(question.id)) {
      dfs(question.id, []);
    }
  });

  return circularChains;
};

/**
 * Validates logic rules and returns warnings/errors
 */
export interface LogicValidationResult {
  isValid: boolean;
  warnings: string[];
  errors: string[];
}

export const validateLogicRules = (
  questions: QuestionDefinition[]
): LogicValidationResult => {
  const warnings: string[] = [];
  const errors: string[] = [];

  // Check for circular dependencies
  const circularDeps = detectCircularDependencies(questions);
  if (circularDeps.length > 0) {
    circularDeps.forEach((cycle) => {
      const questionTitles = cycle.map((qId) => {
        const q = questions.find((question) => question.id === qId);
        return q ? q.title : qId;
      });
      errors.push(
        `Circular dependency detected: ${questionTitles.join(' → ')}`
      );
    });
  }

  // Check for orphaned references
  const questionIds = new Set(questions.map((q) => q.id));
  questions.forEach((question) => {
    if (!question.logicRules || question.logicRules.length === 0) return;

    question.logicRules.forEach((rule) => {
      // Check condition references
      rule.ruleGroups.forEach((group) => {
        group.conditions.forEach((condition) => {
          if (!questionIds.has(condition.questionId)) {
            warnings.push(
              `Logic rule "${rule.name}" in question "${question.title}" references non-existent question: ${condition.questionId}`
            );
          }
        });
      });

      // Check action targets
      rule.actions.forEach((action) => {
        if (action.targetQuestionId && !questionIds.has(action.targetQuestionId)) {
          warnings.push(
            `Logic rule "${rule.name}" in question "${question.title}" targets non-existent question: ${action.targetQuestionId}`
          );
        }
      });
    });
  });

  return {
    isValid: errors.length === 0,
    warnings,
    errors,
  };
};
